import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { SignalSelectionCacher } from '../signal-selection-cacher/signal-selection-cacher';
import { takeUntil } from 'rxjs/operators';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { BsLocaleService } from 'ngx-bootstrap/datepicker';
import { SignalGroupElement, TurbineDataWithSignals, TurbineSignal, TurbineSignalGroup, TurbineSignalGroups } from 'app/shared/models/data-studio.model';
import { checkIncludes } from 'app/data-studio-redesign/helper/data-studio-signal.helper';
import { LanguageService } from 'app/shared/services/language/language.service';

@Component({
  selector: 'app-turbines-signals',
  templateUrl: './turbines-signals.component.html',
  styleUrls: ['./turbines-signals.component.scss'],
  standalone: false
})
export class TurbinesSignalsComponent implements OnChanges, OnInit, OnDestroy {

  @Input() turbineSignals: TurbineDataWithSignals[] = [];
  @Input() signalTypesMapper: Map<string, string[]>;
  @Output() changeSignalsSelection = new EventEmitter<TurbineSignal[]>();

  selectAllSignalsFlag: boolean = false;
  totalSignalsSelected: number = 0;
  totalSignals: number = 0;
  signalGroupArray: SignalGroupElement[] = [];
  signalGroupSelected: Record<TurbineSignalGroup, number> = {
    [TurbineSignalGroups.ambient]: 0,
    [TurbineSignalGroups.electrical]: 0,
    [TurbineSignalGroups.technical]: 0,
    [TurbineSignalGroups.temperature]: 0
  };
  turbineTypeByPark: string[] = [];

  private unsubscribe$: Subject<boolean> = new Subject();

  constructor(
    private selectedSignalsCacher: SignalSelectionCacher,
    private translateService: TranslateService,
    private localeService: BsLocaleService,
    private languageService: LanguageService
  ) { }

  ngOnInit(): void {
    this.subscribeLanguageChange();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.turbineSignals?.currentValue) {
      this.turbineTypeByPark = [...new Set(this.turbineSignals.map(turbine => turbine.type))];
      if (!this.signalGroupArray.length) {
        this.resetSignalArray();
      }
      this.removeAbsentSignals();
      this.fillSignalArray();
      this.selectedSignalsCacher.restoreSelectedSignals(this.signalGroupArray);
      this.checkSelectedSignals();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next(true);
    this.unsubscribe$.unsubscribe();
  }

  selectAllSignals() {
    this.selectAllSignalsFlag = !this.selectAllSignalsFlag;
    this.signalGroupArray.forEach(element => {
      element.signalGroupName.checked = this.selectAllSignalsFlag;
      element.signals.forEach(signal => {
        signal.checked = this.selectAllSignalsFlag;
      });
      this.signalGroupSelected[element.signalGroupName.name] = this.selectAllSignalsFlag ? element.signals.length : 0;
    });
    this.totalSignalsSelected = this.signalGroupArray.flatMap(e => e.signals).filter(data => data.checked === true).length;
    this.setSelectedTurbineSignals();
  }

  private fillSignalArray() {
    this.totalSignals = 0;

    this.turbineSignals.forEach(turbineSignals => {
      Object.values(TurbineSignalGroups).forEach(groupName => {
        const signalGroup = this.getSignalGroupByName(groupName);
        this.addUniqueSignals(turbineSignals[groupName], signalGroup, turbineSignals.type);
      });
    });

    this.totalSignals = this.signalGroupArray.flatMap(e => e.signals).length;
    this.signalGroupArray = this.sortTranslateSignals(this.signalGroupArray);
  }

  selectAllSignalsByGroup(groupName) {
    this.signalGroupArray.forEach(element => {
      if (element.signalGroupName.name === groupName) {
        element.signals.forEach(signal => {
          signal.checked = element.signalGroupName.checked;
        });
        this.signalGroupSelected[element.signalGroupName.name] = element.signals.filter(data => data.checked === true).length;
      }
    });
    this.totalSignalsSelected = this.signalGroupArray.flatMap(e => e.signals).filter(data => data.checked === true).length;
    this.selectAllSignalsFlag = this.totalSignalsSelected === this.totalSignals ? true : false;
    this.setSelectedTurbineSignals();
  }

  checkSelectedSignals(): void {
    this.signalGroupArray.forEach(groupElement => {
    const groupName = groupElement.signalGroupName.name as TurbineSignalGroup;

    if (groupName in TurbineSignalGroups) {
      const checkedCount = this.getCheckedCountInGroup(groupElement);

      this.signalGroupSelected[groupName] = checkedCount;
      groupElement.signalGroupName.checked = groupElement.signals.length === checkedCount;
    }
  });
    this.totalSignalsSelected = this.signalGroupArray.flatMap(e => e.signals).filter(data => data.checked === true).length;
    this.selectAllSignalsFlag = this.totalSignalsSelected === this.totalSignals ? true : false;
    this.setSelectedTurbineSignals();
  }

  checkSignalTurbineType(name: string): boolean {
    const availableTypes = this.signalTypesMapper?.get(name) || [];
    return checkIncludes(availableTypes, this.turbineTypeByPark);
  }

  typesDiff(name: string): string {
    this.turbineTypeByPark
    return this.signalTypesMapper?.get(name)?.filter(item => this.turbineTypeByPark.includes(item)).join(', ');
  }

  private getCheckedCountInGroup(groupElement: SignalGroupElement): number {
    return groupElement.signals.filter(signal => signal.checked === true).length;
  }

  private resetSignalArray() {
    this.totalSignalsSelected = 0;
    this.selectAllSignalsFlag = false;
    this.totalSignals = 0;
    this.signalGroupArray = [];
    for (let key in this.signalGroupSelected) {
      this.signalGroupSelected[key] = 0;
      this.signalGroupArray.push({signalGroupName: {checked: false, name: key as TurbineSignalGroup}, signals: []});
    }
  }

  private subscribeLanguageChange() {
    this.languageService.updateCalendars(this.translateService.currentLang,this.localeService);
    this.translateService.onLangChange.pipe(takeUntil(this.unsubscribe$)).subscribe(event => {
      if (event) {
        this.languageService.updateCalendars(event.lang,this.localeService);
        this.signalGroupArray = this.sortTranslateSignals(this.signalGroupArray);
      }
    });
  }

  private sortTranslateSignals(signalGroupArray){
    return signalGroupArray.map(item => ({
      ...item,
      signals: item.signals.sort((a,b) => {
        const translatedA = this.translateService.instant(a.name) || a.name;
        const translatedB = this.translateService.instant(b.name) || b.name;
        return translatedA.localeCompare(translatedB);
      })
    })).filter(item => !!item.signals.length)
  }

  private getSignalGroupByName(groupName: TurbineSignalGroup): SignalGroupElement {
    const group = this.signalGroupArray.find(g => g.signalGroupName.name === groupName);
    if (!group) {
      throw new Error(`Signal group ${groupName} not found`);
    }
    return group;
  }

  private addUniqueSignals(signals: TurbineSignal[], targetGroup: SignalGroupElement, turbineType): void {
    const existingValues = new Set(targetGroup.signals.map(s => s.value));
    const newSignals = signals.filter(s => !existingValues.has(s.name));

    newSignals.forEach(signal => {
      targetGroup.signals.push(this.createProcessedSignal(signal, turbineType));
    });
  }

  private createProcessedSignal(signal: TurbineSignal, turbineType: string): TurbineSignal {
    return {
      checked: false,
      description: `SIGNALS.${signal.name}.DESCRIPTION`,
      deviceId: signal.deviceId,
      name: `SIGNALS.${signal.name}.DISPLAY_NAME`,
      value: signal.name,
      unit: signal.unit,
      turbineType
    };
  }

  private removeAbsentSignals() {
    const inputSignals =  Object.values(TurbineSignalGroups)
      .flatMap(group => this.turbineSignals.flatMap(turbine => turbine[group]))
      .map(signal => signal.name);
    this.signalGroupArray.forEach(group => {
      const existingValues = new Set(inputSignals);
      group.signals = group.signals.filter(s => existingValues.has(s.value));
    });
  }

  private setSelectedTurbineSignals() {
    const signals = this.signalGroupArray.flatMap(e => e.signals).filter(data => data.checked === true);
    this.changeSignalsSelection.emit(signals);
  }



}
